import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { Helmet } from 'react-helmet-async';

import {
  Box,
  TextField,
  CircularProgress,
  Typography,
  InputAdornment,
  Button,
} from '@mui/material';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';

import verifyToken from '../../utils/verifyToken';

import { passwordResetSchema } from '../../validation/schemas/passwordResetSchema';
import { PasswordResetRequest } from '../../services/authentication.types';
import api from '../../services/api';

import InvalidToken from './Confirmations/InvalidToken';
import ChangedPasswordWithSuccess from './Confirmations/ChangedPasswordWithSuccess';

interface TypePasswordReset {
  token: string,
  password: string,
  passwordConfirm: string
}

function PasswordReset() {
  // variables
  const { token } = useParams();
  const [isValidToken, setIsValidToken] = useState(false);
  const [changedPasswordWithSucces, setChangedPasswordWithSucces] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isShowingPassword, setIsShowingPassword] = useState<Array<boolean>>([false, false]);

  const [newPasswordError, setNewPasswordError] = useState('');
  const [newPasswordConfirmError, setNewPasswordConfirmError] = useState('');

  // form
  const {
    register: addFormValues,
    handleSubmit,
    formState: { errors },
  } = useForm<PasswordResetRequest>({
    mode: 'onSubmit',
    // resolver: yupResolver(passwordResetSchema),
    criteriaMode: 'all',
    defaultValues: {
      token: '',
      password: '',
      passwordConfirm: '',
    },
  });

  // effects
  // verify token when page loads or changes
  useEffect(() => {
    if (token) {
      setIsLoading(true);
      verifyToken(token, setIsValidToken, setIsLoading);
    }
  }, [token]);

  // functions
  const handleChangePassConfirm = handleSubmit(async (data: TypePasswordReset) => {
    api.post(`/auth/password-reset/${token}`, {
      token,
      password: data.password,
      passwordConfirm: data.passwordConfirm,
    })
      .then((response) => {
        const { isTokenValid } = response.data;
        if (isTokenValid) {
          setChangedPasswordWithSucces(true);
          return;
        }

        setIsValidToken(false);
      }).catch((error) => {
        const { password, passwordConfirm } = error.response.data.errors;
        console.log(error.response.data);
        setNewPasswordError(password);
        setNewPasswordConfirmError(passwordConfirm);
      }).finally(() => {
        setIsLoading(false);
      });
  });

  const handleShowPassword = (event: React.MouseEvent) => {
    switch (event.currentTarget.id) {
      case '1':
        setIsShowingPassword([!isShowingPassword[0], isShowingPassword[1]]);
        break;
      case '2':
        setIsShowingPassword([isShowingPassword[0], !isShowingPassword[1]]);
        break;
      default:
        setIsShowingPassword([false, false]);
        break;
    }
  };

  // render conditions
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isValidToken) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <InvalidToken />
      </Box>
    );
  }

  if (changedPasswordWithSucces) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <ChangedPasswordWithSuccess />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>パスワード再設定 - BUILD-SELF</title>
      </Helmet>
      <main className="d-flex w-100 h-100 login-background">
        <div className="container d-flex flex-column">
          <div className="vh-100">
            <div className="mx-auto d-table h-100 w-50">
              <div className="d-table-cell align-middle">
                <Box className="text-center my-4" sx={{ width: '100%' }}>
                  <Box>
                    <img
                      className="img-fluid mb-4"
                      width="180"
                      height="48"
                      src="/img/logo.png"
                      alt="ビルドセルフ - BUILD-SELF"
                    />
                    <h1 className="text-bold my-3">
                      パスワード再設定
                    </h1>
                    <Typography
                      fontSize={12}
                    >
                      新しいパスワードをご入力いただき、パスワードを再設定してください。
                    </Typography>
                  </Box>
                </Box>

                <Box className="card">
                  <Box className="card-body">
                    <label className="form-label w-100">
                      新しいパスワード
                    </label>
                    <TextField
                      {...addFormValues('password')}
                      size="small"
                      placeholder="新しいパスワード"
                      variant="outlined"
                      error={!!newPasswordError}
                      helperText={newPasswordError}
                      type={isShowingPassword[0] ? 'text' : 'password'}
                      fullWidth
                      sx={{
                        marginBottom: '1rem',
                      }}
                      InputProps={{
                        endAdornment:
                          <InputAdornment
                            id="1"
                            onClick={handleShowPassword}
                            position="end"
                          >
                            <FontAwesomeIcon icon={isShowingPassword[0] ? faEyeSlash : faEye} />
                          </InputAdornment>,
                      }}
                    />

                    <label className="form-label w-100">
                      新しいパスワード確認
                    </label>
                    <TextField
                      {...addFormValues('passwordConfirm')}
                      size="small"
                      placeholder="新しいパスワード（確認）"
                      variant="outlined"
                      error={!!newPasswordConfirmError}
                      helperText={newPasswordConfirmError}
                      type={isShowingPassword[1] ? 'text' : 'password'}
                      fullWidth
                      InputProps={{
                        endAdornment:
                          <InputAdornment
                            id="2"
                            onClick={handleShowPassword}
                            position="end"
                          >
                            <FontAwesomeIcon icon={isShowingPassword[1] ? faEyeSlash : faEye} />
                          </InputAdornment>,
                      }}
                    />

                    <Button
                      variant="contained"
                      className="w-100"
                      onClick={handleChangePassConfirm}
                    >
                      パスワードを再設定する
                    </Button>
                  </Box>
                </Box>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
export default PasswordReset;
